import {
  DollarSign,
  Menu,
  ShoppingCart,
  Sparkles,
  Star,
  User,
  Users,
  UsersRound,
} from 'lucide-react';
import {
  resolveDailyPromoItem,
  resolveHiddenPromoItem,
} from '@/lib/assistant/resolveBusinessSpecialPromos';
import { buildCatalogDisplayItems } from '@/lib/menu/catalogPromos';

/** Prioriza productos marcados como sugeridos para ticket; sin config no cambia el orden relativo. */
export function orderWithSuggestedFirst(list, business) {
  const idKey = (x) => (x == null || x === '' ? '' : String(x));
  const ids = business?.suggested_product_ids || [];
  if (!ids?.length || !list?.length) return list;
  const idSet = new Set(ids.map(idKey));
  const suggested = ids
    .map((id) => list.find((p) => idKey(p.id) === idKey(id)))
    .filter(Boolean);
  const rest = list.filter((p) => !idSet.has(idKey(p.id)));
  return [...suggested, ...rest];
}

/** `promo:id`, `product:id` o id suelto (compatibilidad con datos antiguos). */
export function parsePromoProductRef(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return { kind: null, id: null };
  if (s.startsWith('promo:')) return { kind: 'promo', id: s.slice(6).trim() };
  if (s.startsWith('product:')) return { kind: 'product', id: s.slice(8).trim() };
  return { kind: 'legacy', id: s };
}

/**
 * Resuelve la selección de config (promo oculta / oferta del día) a un ítem para cards del asistente.
 * @returns {{ item: object, dedupePromoId: string|null }|null}
 */
export function resolveMenuPickForAssistant(ref, promos, products) {
  if (!ref.kind) return null;
  if (ref.kind === 'product') {
    const prod = products.find((p) => p.id === ref.id && p.availability !== 'hidden');
    return prod ? { item: prod, dedupePromoId: null } : null;
  }
  const promo = promos.find((p) => p.id === ref.id && p.is_active);
  if (promo) {
    return {
      item: { ...promo, price: promo.special_price || 0 },
      dedupePromoId: ref.id,
    };
  }
  if (ref.kind === 'legacy') {
    const prod = products.find((p) => p.id === ref.id && p.availability !== 'hidden');
    if (prod) return { item: prod, dedupePromoId: null };
  }
  return null;
}

/** Señales claras de “para 2” (no “doble hamburguesa” ni “compartir” suelto en marketing). */
function duoTwoPersonSignals(s) {
  return (
    /\b2x1\b/.test(s) ||
    /\b2\s*x\s*1\b/.test(s) ||
    /\bduo\b/.test(s) ||
    /\bdúo\b/.test(s) ||
    /\bpara\s*2\b/.test(s) ||
    /\bpara\s+dos\b/.test(s) ||
    /\bentre\s+dos\b/.test(s) ||
    /\bdos\s+personas\b/.test(s) ||
    /\b2\s*pers\b/.test(s) ||
    /\b2\s*pax\b/.test(s) ||
    /\bpares\b/.test(s)
  );
}

function isComboOrPackName(p) {
  const s = `${p.name || ''} ${p.category || ''}`.toLowerCase();
  return /\bcombo\b/.test(s) || /\bpack\b/.test(s);
}

/** Ítems pensados para ~2: promo-type ya filtra promos; productos solo con señales fuertes. */
function productMatchesDuoHints(p) {
  const s = `${p.name || ''} ${p.category || ''} ${p.description || ''}`.toLowerCase();
  if (duoTwoPersonSignals(s)) return true;
  if (isComboOrPackName(p) && duoTwoPersonSignals(s)) return true;
  return false;
}

function promoMatchesDuoHints(p) {
  const s = `${p.name || ''} ${p.promo_type || ''}`.toLowerCase();
  if (p.promo_type === '2x1' || p.promo_type === 'duo') return true;
  if (/\b2x1\b/.test(s) || /\b2\s*x\s*1\b/.test(s)) return true;
  if (/\bpara\s*2\b/.test(s) || /\bpara\s+dos\b/.test(s)) return true;
  return productMatchesDuoHints({ name: p.name, category: '', description: p.description || '' });
}

/** Grupo / familiar: sin “triple” suelto (ej. triple queso). */
function productMatchesGroupHints(p) {
  const s = `${p.name || ''} ${p.category || ''} ${p.description || ''}`.toLowerCase();
  if (/\bfamiliar\b/.test(s) || /\bgrupo\b/.test(s) || /\bgrupal\b/.test(s)) return true;
  if (/\bpara\s*3\b/.test(s) || /\bpara\s+tres\b/.test(s)) return true;
  if (/\bbig\s*three\b/.test(s)) return true;
  if (/\bthree\b/.test(s) && /\b(big|pack|combo)\b/.test(s)) return true;
  if (
    /\btriple\b/.test(s) &&
    (/\bfamiliar\b/.test(s) || /\bgrupo\b/.test(s) || /\bpara\s*3\b/.test(s) || /\bpack\b/.test(s))
  ) {
    return true;
  }
  if (
    /\bcompartir\b/.test(s) &&
    (/\bfamiliar\b/.test(s) || /\bgrupo\b/.test(s) || /\bpara\s*3\b/.test(s) || /\bpara\s+tres\b/.test(s))
  ) {
    return true;
  }
  return false;
}

function promoMatchesGroupHints(p) {
  const s = `${p.name || ''} ${p.promo_type || ''}`.toLowerCase();
  if (p.promo_type === 'group') return true;
  if (/\bfamiliar\b/.test(s) || /\bgrupo\b/.test(s) || /\bpara\s*3\b/.test(s)) return true;
  return productMatchesGroupHints({ name: p.name, category: '', description: p.description || '' });
}

/**
 * @param {object} deps
 * @param {() => object|null} deps.getMenuData
 * @param {number} [deps.productsPerPage]
 * @param {React.Dispatch<React.SetStateAction<any[]>>} deps.setPendingProducts
 * @param {React.MutableRefObject<any[]>} deps.pendingProductsRef
 * @param {React.Dispatch<React.SetStateAction<string|null>>} deps.setCurrentProductContext
 * @param {React.Dispatch<React.SetStateAction<string>>} deps.setCurrentStep
 * @param {function} deps.addBotMessage
 * @param {function} deps.addUserMessage
 * @param {function} deps.showBackToMenu
 * @param {function} deps.showProductActions
 * @param {function} deps.handleMainChoice
 * @param {function} deps.handleOrderChoice
 * @param {function} deps.handleOrderChoiceFromText
 * @param {function} deps.focusAssistantChatInput
 */
export function createAssistantProductFlow(deps) {
  const {
    getMenuData,
    productsPerPage = 3,
    setPendingProducts,
    pendingProductsRef,
    setCurrentProductContext,
    setCurrentStep,
    addBotMessage,
    addUserMessage,
    showBackToMenu,
    showProductActions,
    handleMainChoice,
    handleOrderChoice,
    handleOrderChoiceFromText,
    focusAssistantChatInput,
  } = deps;

  const showProductsWithPagination = (products, message, context) => {
    if (!products || products.length === 0) {
      addBotMessage('Por ahora no hay nada pa mostrar acá.');
      showBackToMenu();
      return;
    }

    setPendingProducts([]);
    pendingProductsRef.current = [];
    setCurrentProductContext(context);

    const listoAsiOption = {
      label: '✅ Listo así',
      value: 'done',
      icon: null,
      action: () => showProductActions(),
    };

    addBotMessage(
      message,
      products.length > productsPerPage ? [listoAsiOption] : null,
      products,
      {
        productsScrollViewport: products.length > productsPerPage,
        productsScrollVisibleCount: productsPerPage,
        textAfterProducts:
          products.length > productsPerPage
            ? `${products.length} opciones · deslizá para ver más`
            : undefined,
      }
    );

    if (products.length <= productsPerPage) {
      showProductActions();
    }
  };

  /** Compat: ya no pagina por mensajes; el listado scrollea en un solo bloque. */
  const showMoreProducts = () => {
    showProductActions();
  };

  const showProductsForSolo = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const products = menuData?.products || [];
      const soloProducts = products.filter((p) => p.availability !== 'hidden' && p.price <= 15000);

      showProductsWithPagination(soloProducts, '👤 Ideas pa uno:', 'solo');
    }, 500);
  };

  const showProductsForDuo = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const promos = menuData?.promos || [];
      const products = menuData?.products || [];

      const duoPromos = promos.filter((p) => p.is_active && promoMatchesDuoHints(p));

      if (duoPromos.length > 0) {
        const promoProducts = duoPromos.map((p) => ({
          ...p,
          price: p.special_price || p.price || 0,
        }));
        showProductsWithPagination(promoProducts, '👥 Pa compartir (2):', 'duo');
      } else {
        const duoItems = products.filter(
          (p) => p.availability !== 'hidden' && productMatchesDuoHints(p)
        );

        if (duoItems.length > 0) {
          showProductsWithPagination(duoItems, '👥 Opciones claras para 2:', 'duo');
        } else {
          addBotMessage(
            '👥 No tengo promos ni productos marcados para 2 acá. Te conviene revisar el menú completo.'
          );
          showProductActions();
        }
      }
    }, 500);
  };

  const showProductsForGroup = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const promos = menuData?.promos || [];
      const products = menuData?.products || [];

      const groupPromos = promos.filter((p) => p.is_active && promoMatchesGroupHints(p));

      if (groupPromos.length > 0) {
        const promoProducts = groupPromos.map((p) => ({
          ...p,
          price: p.special_price || p.price || 0,
        }));
        showProductsWithPagination(promoProducts, '👨‍👩‍👧‍👦 Pa el grupo:', 'group');
      } else {
        const groupProducts = products.filter(
          (p) => p.availability !== 'hidden' && productMatchesGroupHints(p)
        );

        if (groupProducts.length > 0) {
          showProductsWithPagination(groupProducts, '👨‍👩‍👧‍👦 Pa compartir en grupo:', 'group');
        } else {
          addBotMessage(
            '👨‍👩‍👧‍👦 No veo familiares ni ítems de grupo claros en el menú cargado. Abrí el menú y elegí algo pensado para varias personas, o pedime por nombre si ya sabés qué querés.'
          );
          showProductActions();
        }
      }
    }, 500);
  };

  const showSurpriseMe = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const business = menuData?.business || {};
      const promos = menuData?.promos || [];
      const products = menuData?.products || [];

      let surpriseItems = [];

      const configuredProductIds = business.surprise_product_ids || [];
      if (configuredProductIds.length > 0) {
        const configuredProducts = products.filter(
          (p) => configuredProductIds.includes(p.id) && p.availability !== 'hidden'
        );
        if (configuredProducts.length > 0) {
          setCurrentProductContext('surprise');
          addBotMessage('✨ Partí por estos:', null, configuredProducts);
          showProductActions();
          return;
        }
      }

      const hiddenItem = resolveHiddenPromoItem(menuData);
      if (hiddenItem) {
        surpriseItems.push(hiddenItem);
      }
      const hiddenPromoIdForDedup =
        hiddenItem?.id && String(hiddenItem.id).startsWith('promo:')
          ? String(hiddenItem.id).replace(/^promo:/, '')
          : null;

      const surprisePromos = promos.filter(
        (p) =>
          p.is_active &&
          (p.is_surprise || p.is_hidden || p.promo_type === 'hidden') &&
          (hiddenPromoIdForDedup ? p.id !== hiddenPromoIdForDedup : true)
      );
      surpriseItems = surpriseItems.concat(
        surprisePromos.map((p) => ({
          ...p,
          price: p.special_price || 0,
        }))
      );

      if (surpriseItems.length === 0) {
        const categories = menuData?.categories || [];
        const specialCategoryNames = [
          'sorpréndeme',
          'sorprendeme',
          'sorpresa',
          'promo oculta',
          'especial',
          'secreto',
          'hidden',
        ];
        const specialCategories = categories.filter((c) =>
          specialCategoryNames.some((name) => c.name?.toLowerCase().includes(name))
        );

        if (specialCategories.length > 0) {
          const categoryIds = specialCategories.map((c) => c.id);
          const specialProducts = products.filter(
            (p) => p.availability !== 'hidden' && categoryIds.includes(p.category_id)
          );
          surpriseItems = surpriseItems.concat(specialProducts);
        }
      }

      if (surpriseItems.length === 0) {
        const availableProducts = products.filter((p) => p.availability !== 'hidden');
        const topProducts = [...availableProducts]
          .sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0))
          .slice(0, 3);
        surpriseItems = topProducts;
      }

      if (surpriseItems.length > 0) {
        const hasPromos = surpriseItems.some((item) => item.special_price !== undefined);
        const message = hasPromos
          ? '🎁 Tengo algo piola pa ti (si preguntai 🤫)'
          : '✨ Estos son los favoritos';

        showProductsWithPagination(surpriseItems, message, 'surprise');
      } else {
        addBotMessage('Por ahora no hay nada pa mostrar acá.');
        showProductActions();
      }
    }, 500);
  };

  const showMostOrderedProducts = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const products = menuData?.products || [];
      const list = [...products]
        .filter((p) => p.availability !== 'hidden')
        .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
        .slice(0, 8);
      if (list.length) {
        showProductsWithPagination(list, '🔥 Lo más pedido por acá:', 'popular');
      } else {
        addBotMessage('No tengo registro de ventas para ordenar lo más pedido. Mirá opciones en el chat 👇', [
          {
            label: 'Explorar en el chat',
            value: 'ft-pop-fallback',
            icon: Menu,
            action: () => handleMainChoice('order'),
          },
        ]);
      }
    }, 500);
  };

  const showCheapestProducts = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const products = menuData?.products || [];
      const sortedProducts = [...products]
        .filter((p) => p.availability !== 'hidden')
        .sort((a, b) => (a.discount_price || a.price) - (b.discount_price || b.price));

      showProductsWithPagination(sortedProducts, '💰 Lo más barato:', 'cheap');
    }, 500);
  };

  const showSpecialProducts = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const business = menuData?.business || {};
      const products = menuData?.products || [];
      const promos = menuData?.promos || [];

      let specialItems = [];

      const dailyItem = resolveDailyPromoItem(menuData);
      if (dailyItem) {
        setCurrentProductContext('special');
        addBotMessage('⭐ Promo del día', null, [dailyItem]);
        showProductActions();
        return;
      }

      const premiumProducts = products
        .filter((p) => p.availability !== 'hidden')
        .sort((a, b) => (b.discount_price || b.price) - (a.discount_price || a.price));

      specialItems = specialItems.concat(premiumProducts);

      if (specialItems.length > 0) {
        showProductsWithPagination(specialItems, '⭐ Cosas especiales:', 'special');
      } else {
        addBotMessage('Ahora no tengo especiales pa mostrar.');
        showProductActions();
      }
    }, 500);
  };

  const buildPersonaOrderOptions = () => [
    {
      label: 'Solo para mí',
      value: 'solo',
      icon: User,
      action: () => handleOrderChoice('solo'),
    },
    {
      label: 'Para 2 personas',
      value: 'duo',
      icon: Users,
      action: () => handleOrderChoice('duo'),
    },
    {
      label: 'Para varias personas',
      value: 'group',
      icon: UsersRound,
      action: () => handleOrderChoice('group'),
    },
    {
      label: 'Sorpréndeme',
      value: 'surprise',
      icon: Sparkles,
      action: () => handleOrderChoice('surprise'),
    },
    {
      label: 'Algo económico',
      value: 'cheap',
      icon: DollarSign,
      action: () => handleOrderChoice('cheap'),
    },
    {
      label: 'Algo recomendado',
      value: 'special',
      icon: Star,
      action: () => handleOrderChoice('special'),
    },
  ];

  const sortFullMenuProducts = (menuData) => {
    const products = menuData?.products || [];
    const promos = menuData?.promos || [];
    const dailyDealProduct = menuData?.daily_deal_product || null;
    return buildCatalogDisplayItems({
      products,
      promos,
      selectedCategory: 'all',
      searchQuery: '',
      dailyDealProduct,
      isDemo: false,
    });
  };

  const showOrderMenuBrowse = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const personaOptions = buildPersonaOrderOptions();
      const menuProducts = sortFullMenuProducts(menuData);

      if (!menuProducts.length) {
        addBotMessage('¿Para cuántas personas es?', personaOptions);
        return;
      }

      setCurrentProductContext('full-menu');
      const scrollHint =
        menuProducts.length > productsPerPage
          ? `${menuProducts.length} opciones · deslizá para ver más`
          : '';
      addBotMessage(
        'Explorá el menú 👇 Agregá lo que quieras:',
        personaOptions,
        menuProducts,
        {
          textAfterProducts: scrollHint
            ? `${scrollHint}\n\n¿O prefieres que te recomiende según cuántos sean?`
            : '¿O prefieres que te recomiende según cuántos sean?',
          productsScrollViewport: menuProducts.length > productsPerPage,
          productsScrollVisibleCount: productsPerPage,
        }
      );
    }, 300);
  };

  const showSmartSuggestions = () => {
    setTimeout(() => {
      const menuData = getMenuData();
      const products = menuData?.products || [];
      const categories = menuData?.categories || [];

      if (categories.length === 0) {
        addBotMessage('Todavía no hay categorías acá.');
        showProductActions();
        return;
      }

      const categoryOptions = categories
        .map((category) => {
          const categoryProducts = products.filter(
            (p) => p.category_id === category.id && p.availability !== 'hidden'
          );
          return {
            label: `${category.name} (${categoryProducts.length})`,
            value: category.id,
            icon: Menu,
            action: () => showCategoryProducts(category),
          };
        })
        .filter((opt) => opt.label.includes('(') && !opt.label.includes('(0)'));

      if (categoryOptions.length > 0) {
        addBotMessage('📋 ¿Qué categoría?', categoryOptions);
      } else {
        addBotMessage('Por ahora no hay nada pa mostrar acá.');
        showProductActions();
      }
    }, 500);
  };

  const showCategoryProducts = (category) => {
    addUserMessage(category.name);

    setTimeout(() => {
      const menuData = getMenuData();
      const products = menuData?.products || [];
      const business = menuData?.business || {};
      let categoryProducts = products.filter(
        (p) => p.category_id === category.id && p.availability !== 'hidden'
      );
      categoryProducts = orderWithSuggestedFirst(categoryProducts, business);

      if (categoryProducts.length > 0) {
        showProductsWithPagination(categoryProducts, `📦 ${category.name}:`, `category-${category.id}`);
      } else {
        addBotMessage(`En ${category.name} no hay nada por ahora.`);
        showProductActions();
      }
    }, 500);
  };

  const continueShoppingFromAssistant = () => {
    const menuData = getMenuData();
    if (!menuData) return;
    setCurrentStep('order');
    const products = menuData.products || [];
    const popular = [...products]
      .filter((p) => p.availability !== 'hidden')
      .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
      .slice(0, 6);
    addBotMessage(
      'Seguimos con tu pedido 👇 Elegí cómo explorar el menú o escribí lo que buscás por nombre:',
      [
        {
          label: 'Por categorías',
          value: 'cs-categories',
          icon: Menu,
          action: () => handleOrderChoice('categories'),
        },
        {
          label: 'Lo más pedido',
          value: 'cs-popular',
          icon: Star,
          action: () => handleOrderChoiceFromText('popular'),
        },
        {
          label: 'Promos y especiales',
          value: 'cs-special',
          icon: Sparkles,
          action: () => handleOrderChoice('special'),
        },
        {
          label: 'Lo más barato',
          value: 'cs-cheap',
          icon: DollarSign,
          action: () => handleOrderChoice('cheap'),
        },
        {
          label: 'Más formas de pedir',
          value: 'cs-order-menu',
          icon: ShoppingCart,
          action: () => handleMainChoice('order'),
        },
      ],
      popular.length ? popular : null
    );
    focusAssistantChatInput();
  };

  return {
    showProductsWithPagination,
    showMoreProducts,
    showProductsForSolo,
    showProductsForDuo,
    showProductsForGroup,
    showSurpriseMe,
    showCheapestProducts,
    showSpecialProducts,
    showMostOrderedProducts,
    showSmartSuggestions,
    showCategoryProducts,
    showOrderMenuBrowse,
    continueShoppingFromAssistant,
  };
}
